import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles } from 'lucide-react';
import AIReviewModal from './AIReviewModal';
import { applyReplacement, fetchAiReviewQuota, renewalLabel, requestAiReview, sendAiReviewFeedback } from '../utils/aiReview';

const MIN_CHARS = 20;

// Botão "Revisar com IA". A IA só roda ao clicar; cada sugestão só é aplicada em "Aprovar".
export default function AIReviewButton({ quillRef, reportId }) {
  const [quota, setQuota] = useState(null);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('idle'); // idle | loading | error | done
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [model, setModel] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    fetchAiReviewQuota().then((q) => { if (alive.current) setQuota(q); }).catch(() => {});
    return () => { alive.current = false; };
  }, []);

  const exhausted = quota && quota.used >= quota.limit;
  const renewal = renewalLabel(quota?.renews_at);

  const run = async () => {
    const quill = quillRef.current?.getEditor?.();
    const text = quill ? quill.getText() : '';
    setOpen(true);
    setItems([]);
    if (text.trim().length < MIN_CHARS) {
      setError('Escreva o laudo antes de pedir a revisão.');
      setState('error');
      return;
    }
    setError('');
    setState('loading');
    try {
      const result = await requestAiReview(reportId, text);
      if (!alive.current) return;
      setModel(result.model || null);
      setItems(result.suggestions.map((s) => ({ ...s, status: 'pending' })));
      setQuota(result.quota);
      setState('done');
    } catch (err) {
      if (!alive.current) return;
      if (err.quota) setQuota(err.quota);
      setError(err.message);
      setState('error');
    }
  };

  const patch = (id, status) => setItems((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));

  const decide = useCallback((item, decision) => {
    const flags = item.flags || [];
    if (decision === 'approved') {
      const quill = quillRef.current?.getEditor?.();
      const ok = quill ? applyReplacement(quill, item.original, item.sugerido) : false;
      if (!ok) {
        patch(item.id, 'notfound');
        sendAiReviewFeedback({ decision: 'rejected', categoria: item.categoria, tipo: item.tipo, flags: [...flags, 'trecho_nao_encontrado'], model });
        return;
      }
      patch(item.id, 'approved');
    } else {
      patch(item.id, 'dismissed');
    }
    sendAiReviewFeedback({ decision, categoria: item.categoria, tipo: item.tipo, flags, model });
  }, [quillRef, model]);

  return (
    <>
      {/* Fixo na tela: o médico pode pedir a revisão de qualquer ponto do laudo. Fica acima da barra de emissão e do WhatsApp. Portal: nenhum ancestral animado prende o fixed. */}
      {createPortal(
        <button
          type="button"
          onClick={run}
          disabled={exhausted}
          title={exhausted ? `Revisões esgotadas${renewal ? `. Renova em ${renewal}.` : '.'}` : undefined}
          aria-label="Revisar com IA"
          className="fixed bottom-28 right-5 z-50 flex min-h-[48px] items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
        >
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          <span>Revisar com IA</span>
          {quota && (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-xs tabular-nums" aria-live="polite">
              {Math.max(quota.limit - quota.used, 0)}/{quota.limit}
            </span>
          )}
        </button>
      , document.body)}
      {open && (
        <AIReviewModal
          state={state}
          error={error}
          items={items}
          quota={quota}
          onApprove={(item) => decide(item, 'approved')}
          onReject={(item) => decide(item, 'rejected')}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
